import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Building2, MapPin, Phone, Mail, Globe, Search, Loader2, ArrowLeft, BadgeCheck, Filter, Star, MessageCircle, Play, Image, ExternalLink, ChevronDown, Clock, DollarSign, Users, Send, Crown, Eye, Home, ChevronRight, GitBranch } from 'lucide-react';
import { trackPageView, trackWhatsAppClick, trackCallClick, trackMapsClick, trackContactSent, trackPhoneReveal } from '../services/analytics';
import { InfoBadge } from '../components/TreatmentInfo';
import { CompareCheckbox } from '../components/CompareBar';
import CentersByLocationLinks from '../components/seo/CentersByLocationLinks';
import { CHILEAN_REGIONS } from '../constants/chileanLocations';
import { API } from '../utils/api';
import { toast } from 'sonner';

const cities = ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Arica', 'Puerto Montt'];

// Helper function to get the correct image URL
function getImageUrl(url) {
  if (!url) return null;
  // Strip old preview domain URLs - extract just the /api/ path
  if (url.includes('.emergent.host/api/') || url.includes('.emergentagent.com/api/')) {
    try { url = new URL(url).pathname; } catch(e) {}
  }
  // If it's a local API URL, prepend the backend URL
  if (url.startsWith('/api/')) {
    var base = process.env.REACT_APP_BACKEND_URL;
    if (base && !base.includes('localhost') && typeof window !== 'undefined' && !base.includes(window.location.hostname)) {
      base = window.location.origin;
    }
    return (base || '') + url;
  }
  return url;
}

// Helper function to generate URL-friendly slugs
function generateSlug(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^\w\s-]/g, '') // Remove special chars
    .replace(/[\s_]+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove multiple hyphens
    .trim()
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

// Helper function to parse modality (handles both array and concatenated strings)
function parseModality(modality) {
  if (!modality) return [];
  if (Array.isArray(modality)) return modality;
  
  // If it's a string, try to split it by common modality names
  var modalityStr = String(modality).toLowerCase();
  var result = [];
  
  // Check for each modality type in the string
  if (modalityStr.includes('residencial')) result.push('Residencial');
  if (modalityStr.includes('ambulatorio')) result.push('Ambulatorio');
  if (modalityStr.includes('online') || modalityStr.includes('línea')) result.push('Online');
  if (modalityStr.includes('domicilio')) result.push('A domicilio');
  if (modalityStr.includes('semi')) result.push('Semi-residencial');
  
  // If nothing was found, return the original as single item
  if (result.length === 0 && modality) result.push(modality);
  
  return result;
}

function StarRating({ rating, onRate, interactive = false, size = 'md' }) {
  var sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(function(star) {
        return (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? function() { onRate(star); } : undefined}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
            disabled={!interactive}
          >
            <Star
              className={sizeClass + ' ' + (star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600')}
            />
          </button>
        );
      })}
    </div>
  );
}

function WhatsAppButton({ number, name, targetType, targetId }) {
  if (!number) return null;
  var cleanNumber = number.replace(/\D/g, '');
  if (!cleanNumber.startsWith('56')) cleanNumber = '56' + cleanNumber;
  var message = encodeURIComponent('Hola, los contacto desde sinadicciones.org. Estoy interesado en ' + name + '. ¿Podrían darme más información?');
  var url = 'https://wa.me/' + cleanNumber + '?text=' + message;
  
  function handleClick() {
    if (targetType && targetId) {
      trackWhatsAppClick(targetType, targetId);
    }
  }
  
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={handleClick} className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl hover:bg-green-500/20 transition-colors duration-200 text-green-400">
      <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
    </a>
  );
}

function GoogleMapsLink({ address, city, targetType, targetId }) {
  if (!address && !city) return null;
  var query = encodeURIComponent((address || '') + ', ' + (city || '') + ', Chile');
  var url = 'https://www.google.com/maps/search/?api=1&query=' + query;
  
  function handleClick() {
    if (targetType && targetId) {
      trackMapsClick(targetType, targetId);
    }
  }
  
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={handleClick} className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
      <MapPin className="w-5 h-5 text-emerald-400" /><span>Ver en Google Maps</span><ExternalLink className="w-4 h-4 ml-auto" />
    </a>
  );
}

function YouTubeEmbed({ url }) {
  if (!url) return null;
  var videoId = '';
  if (url.includes('youtube.com/watch')) {
    videoId = url.split('v=')[1]?.split('&')[0];
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  }
  if (!videoId) return null;
  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-slate-800">
      <iframe
        src={'https://www.youtube.com/embed/' + videoId}
        title="Video del centro"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  );
}

function ContactForm({ targetType, targetId, targetName }) {
  var [open, setOpen] = useState(false);
  var [name, setName] = useState('');
  var [email, setEmail] = useState('');
  var [phone, setPhone] = useState('');
  var [message, setMessage] = useState('');
  var [loading, setLoading] = useState(false);
  var [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setLoading(true);
    try {
      await axios.post(API + '/' + targetType + 's/' + targetId + '/contact', {
        sender_name: name,
        sender_email: email,
        sender_phone: phone || null,
        message: message
      });
      setSuccess(true);
      toast.success('Mensaje enviado');
      // Track contact sent
      trackContactSent(targetType, targetId);
    } catch (e) {
      toast.error('Error al enviar mensaje');
    }
    setLoading(false);
  }

  if (success) {
    return (
      <div className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center">
        <p className="text-emerald-400 font-medium">¡Mensaje enviado!</p>
        <p className="text-slate-400 text-sm mt-1">Te contactarán pronto</p>
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 justify-between">
          <span className="flex items-center gap-2"><Mail className="w-4 h-4" />Enviar mensaje</span>
          <ChevronDown className={'w-4 h-4 transition-transform ' + (open ? 'rotate-180' : '')} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4">
        <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-slate-800/50 rounded-xl">
          <div>
            <Label className="text-slate-300">Tu nombre *</Label>
            <Input value={name} onChange={function(e) { setName(e.target.value); }} placeholder="Nombre completo" className="bg-slate-950 border-slate-700 text-slate-200" />
          </div>
          <div>
            <Label className="text-slate-300">Tu email *</Label>
            <Input type="email" value={email} onChange={function(e) { setEmail(e.target.value); }} placeholder="correo@ejemplo.com" className="bg-slate-950 border-slate-700 text-slate-200" />
          </div>
          <div>
            <Label className="text-slate-300">Tu teléfono</Label>
            <Input value={phone} onChange={function(e) { setPhone(e.target.value); }} placeholder="+56 9 1234 5678" className="bg-slate-950 border-slate-700 text-slate-200" />
          </div>
          <div>
            <Label className="text-slate-300">Mensaje *</Label>
            <Textarea value={message} onChange={function(e) { setMessage(e.target.value); }} placeholder={'Hola, me gustaría obtener más información sobre ' + targetName + '...'} className="bg-slate-950 border-slate-700 text-slate-200 min-h-[100px]" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Enviar mensaje
          </Button>
        </form>
      </CollapsibleContent>
    </Collapsible>
  );
}

var modalityLabels = { residencial: 'Residencial', ambulatorio: 'Ambulatorio', online: 'Online', mixto: 'Mixto' };
var genderLabels = { hombre: 'Solo Hombres', hombres: 'Solo Hombres', mujer: 'Solo Mujeres', mujeres: 'Solo Mujeres', mixto: 'Mixto', todos: 'Mixto' };

function formatPrice(price) {
  if (!price) return null;
  return '$' + price.toLocaleString('es-CL');
}

function ReviewForm({ targetType, targetId, onSuccess }) {
  var [name, setName] = useState('');
  var [rating, setRating] = useState(0);
  var [comment, setComment] = useState('');
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');
  var [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Ingresa tu nombre'); return; }
    if (rating === 0) { setError('Selecciona una calificación'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(API + '/' + targetType + 's/' + targetId + '/reviews', {
        reviewer_name: name,
        rating: rating,
        comment: comment || null
      });
      setSuccess(true);
      setName('');
      setRating(0);
      setComment('');
      if (onSuccess) onSuccess();
    } catch (e) {
      setError('Error al enviar la reseña');
    }
    setLoading(false);
  }

  if (success) {
    return (
      <div className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center">
        <p className="text-emerald-400 font-medium">¡Gracias por tu reseña!</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium text-slate-400 mb-2 block">Tu nombre</label>
        <Input value={name} onChange={function(e) { setName(e.target.value); }} placeholder="Nombre" className="bg-slate-950 border-slate-700 text-slate-200" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-400 mb-2 block">Calificación</label>
        <StarRating rating={rating} onRate={setRating} interactive={true} size="lg" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-400 mb-2 block">Comentario (opcional)</label>
        <Textarea value={comment} onChange={function(e) { setComment(e.target.value); }} placeholder="Cuéntanos tu experiencia..." className="bg-slate-950 border-slate-700 text-slate-200 min-h-[100px]" />
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Enviar reseña
      </Button>
    </form>
  );
}

function ReviewsList({ targetType, targetId }) {
  var [reviews, setReviews] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showAll, setShowAll] = useState(false);
  var VISIBLE_COUNT = 5;

  useEffect(function() {
    loadReviews();
  }, [targetType, targetId]);

  async function loadReviews() {
    try {
      var res = await axios.get(API + '/' + targetType + 's/' + targetId + '/reviews');
      setReviews(res.data.reviews || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;

  if (reviews.length === 0) return <p className="text-slate-500 text-center py-4">Aún no hay reseñas</p>;

  var visibleReviews = showAll ? reviews : reviews.slice(0, VISIBLE_COUNT);
  var hasMore = reviews.length > VISIBLE_COUNT;

  return (
    <div className="space-y-4">
      {visibleReviews.map(function(review) {
        return (
          <div key={review.review_id} className="p-4 bg-slate-800/50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-200">{review.reviewer_name}</span>
                {review.source === 'google_places' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </span>
                )}
              </div>
              <StarRating rating={review.rating} size="sm" />
            </div>
            {review.relative_time && review.source === 'google_places' && (
              <p className="text-slate-500 text-xs mb-2">{review.relative_time}</p>
            )}
            {review.comment && <p className="text-slate-400 text-sm">{review.comment}</p>}
          </div>
        );
      })}
      {hasMore && !showAll && (
        <button 
          onClick={function() { setShowAll(true); }}
          className="w-full py-3 text-center text-emerald-400 hover:text-emerald-300 font-medium border border-slate-700 rounded-xl hover:bg-slate-800/50 transition-colors"
          data-testid="reviews-show-more"
        >
          Ver más reseñas ({reviews.length - VISIBLE_COUNT} más)
        </button>
      )}
      {showAll && hasMore && (
        <button 
          onClick={function() { setShowAll(false); }}
          className="w-full py-3 text-center text-slate-400 hover:text-slate-300 font-medium border border-slate-700 rounded-xl hover:bg-slate-800/50 transition-colors"
          data-testid="reviews-show-less"
        >
          Ver menos
        </button>
      )}
    </div>
  );
}

var modalityOptions = [
  { value: 'residencial', label: 'Residencial' },
  { value: 'ambulatorio', label: 'Ambulatorio' },
  { value: 'online', label: 'Online' },
  { value: 'mixto', label: 'Mixto' }
];

var genderOptions = [
  { value: 'hombre', label: 'Solo Hombres' },
  { value: 'mujer', label: 'Solo Mujeres' },
  { value: 'mixto', label: 'Mixto' }
];

// Filters Sidebar Component
function FiltersContent({ region, setRegion, modality, setModality, gender, setGender, priceMin, setPriceMin, priceMax, setPriceMax, verifiedOnly, setVerifiedOnly, clearFilters, setPage }) {
  return (
    <div className="space-y-6">
      {/* Región */}
      <div>
        <label className="text-sm font-medium text-slate-300 mb-2 block">Región</label>
        <Select value={region || 'all'} onValueChange={function(v) { setRegion(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
            <SelectValue placeholder="Todas" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-slate-300">Todas las regiones</SelectItem>
            {CHILEAN_REGIONS.map(function(r) { return <SelectItem key={r.value} value={r.value} className="text-slate-300">{r.label}</SelectItem>; })}
          </SelectContent>
        </Select>
      </div>
      
      {/* Modalidad */}
      <div>
        <label className="text-sm font-medium text-slate-300 mb-2 block">Modalidad</label>
        <Select value={modality || 'all'} onValueChange={function(v) { setModality(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
            <SelectValue placeholder="Todas" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-slate-300">Todas</SelectItem>
            {modalityOptions.map(function(m) { return <SelectItem key={m.value} value={m.value} className="text-slate-300">{m.label}</SelectItem>; })}
          </SelectContent>
        </Select>
      </div>
      
      {/* Género */}
      <div>
        <label className="text-sm font-medium text-slate-300 mb-2 block">Género</label>
        <Select value={gender || 'all'} onValueChange={function(v) { setGender(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-slate-300">Todos</SelectItem>
            {genderOptions.map(function(g) { return <SelectItem key={g.value} value={g.value} className="text-slate-300">{g.label}</SelectItem>; })}
          </SelectContent>
        </Select>
      </div>
      
      {/* Rango de Precios */}
      <div>
        <label className="text-sm font-medium text-slate-300 mb-2 block">Rango de precio (CLP/mes)</label>
        <div className="flex gap-2">
          <Input 
            type="number" 
            placeholder="Desde" 
            value={priceMin} 
            onChange={function(e) { setPriceMin(e.target.value); setPage(1); }} 
            className="bg-slate-800 border-slate-700 text-slate-200 text-sm" 
          />
          <Input 
            type="number" 
            placeholder="Hasta" 
            value={priceMax} 
            onChange={function(e) { setPriceMax(e.target.value); setPage(1); }} 
            className="bg-slate-800 border-slate-700 text-slate-200 text-sm" 
          />
        </div>
        <p className="text-xs text-slate-500 mt-1">Ej: 500.000 - 2.000.000</p>
      </div>
      
      {/* Verificados */}
      <div className="flex items-center gap-2">
        <input 
          type="checkbox" 
          id="verified-filter" 
          checked={verifiedOnly} 
          onChange={function(e) { setVerifiedOnly(e.target.checked); setPage(1); }} 
          className="w-4 h-4 accent-emerald-500" 
        />
        <label htmlFor="verified-filter" className="text-sm text-slate-300 cursor-pointer flex items-center gap-1">
          <BadgeCheck className="w-4 h-4 text-emerald-400" />Solo verificados
        </label>
      </div>
      
      {/* Limpiar */}
      <Button onClick={clearFilters} variant="outline" className="w-full border-slate-700 text-slate-400 hover:text-slate-200">
        Limpiar filtros
      </Button>
    </div>
  );
}

// Center Card Component
function CenterCard({ center }) {
  // Use slug from database if available, otherwise fallback to center_id
  var centerSlug = center.slug || center.center_id;
  var modalityLabel = center.modality ? (Array.isArray(center.modality) ? center.modality[0] : center.modality) : '';
  // Get image from logo, gallery, or images array and proxy Google URLs
  var rawImage = center.logo || (center.gallery && center.gallery[0]) || (center.images && center.images[0]) || null;
  var centerImage = getImageUrl(rawImage);
  
  return (
    <Link to={'/centros/' + centerSlug} data-testid={'center-card-' + center.center_id}>
      <Card className="bg-white border-slate-200 hover:border-emerald-500/50 hover:shadow-lg transition-all duration-300 h-full overflow-hidden group">
        {/* Image */}
        <div className="aspect-video bg-slate-100 relative overflow-hidden">
          {centerImage ? (
            <img src={centerImage} alt={center.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={function(e) { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
          ) : null}
          <div className={'w-full h-full items-center justify-center ' + (centerImage ? 'hidden' : 'flex')}>
            <Building2 className="w-16 h-16 text-slate-300" />
          </div>
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {center.plan_level >= 3 && (
              <span className="px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                <Crown className="w-3 h-3" /> Elite
              </span>
            )}
            {center.plan_level === 2 && (
              <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
            {center.plan_level === 1 && (
              <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                <Crown className="w-3 h-3" /> Destacado
              </span>
            )}
            {!center.plan_level && center.is_premium && (
              <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
            {center.is_branch && center.branch_of && (
              <span className="px-2 py-1 bg-purple-700 text-white text-xs font-medium rounded-md flex items-center gap-1 shadow-md">
                <GitBranch className="w-3 h-3" /> Sucursal de {center.branch_of}
              </span>
            )}
            {center.is_verified && (
              <span className="px-2 py-1 bg-emerald-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                <BadgeCheck className="w-3 h-3" /> Verificado
              </span>
            )}
          </div>
          {/* Rating */}
          {center.average_rating > 0 && (
            <div className="absolute bottom-3 right-3 px-2 py-1 bg-slate-900/80 text-white text-xs rounded-md flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
              {center.average_rating.toFixed(1)} ({center.total_reviews})
            </div>
          )}
          {/* Modality badge with info popup */}
          {modalityLabel && (
            <div className="absolute bottom-3 left-3">
              <InfoBadge tipo="modalidad" valor={modalityLabel} />
            </div>
          )}
        </div>
        
        <CardContent className="p-5">
          {/* Location */}
          <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
            <MapPin className="w-4 h-4 text-emerald-500" />
            <span>{center.city || 'Chile'}</span>
          </div>
          
          {/* Name */}
          <h3 className="font-semibold text-lg text-slate-800 mb-2 line-clamp-2 group-hover:text-emerald-600 transition-colors">
            {center.name}
          </h3>
          
          {/* Modality badges */}
          {center.modality && parseModality(center.modality).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {parseModality(center.modality).map(function(mod, idx) {
                return <InfoBadge key={idx} tipo="modalidad" valor={mod} />;
              })}
            </div>
          )}
          
          {/* Specializations */}
          {center.specializations && center.specializations.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {center.specializations.slice(0, 3).map(function(spec, idx) {
                var specColors = {
                  'Alcohol': 'bg-amber-100 text-amber-700',
                  'Drogas': 'bg-red-100 text-red-700',
                  'Ludopatía': 'bg-pink-100 text-pink-700'
                };
                return (
                  <span key={idx} className={'text-xs px-2 py-0.5 rounded-full ' + (specColors[spec] || 'bg-slate-100 text-slate-600')}>
                    {spec}
                  </span>
                );
              })}
            </div>
          )}
          
          {/* Therapy Approaches - Treatment Type */}
          {center.therapy_approaches && center.therapy_approaches.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {center.therapy_approaches.slice(0, 2).map(function(approach, idx) {
                return <InfoBadge key={idx} tipo="enfoque" valor={approach} />;
              })}
            </div>
          )}
          
          {/* Price */}
          {center.monthly_price ? (
            <div className="flex items-center gap-1 text-emerald-700 text-sm font-medium mb-2">
              <DollarSign className="w-4 h-4" />
              Desde ${center.monthly_price.toLocaleString('es-CL')}/mes
            </div>
          ) : center.is_free ? (
            <div className="flex items-center gap-1 text-green-600 text-sm font-medium mb-2">
              <DollarSign className="w-4 h-4" />
              Gratuito
            </div>
          ) : null}
          
          {/* Footer */}
          <div className="flex items-center gap-3 text-xs text-slate-500 pt-2 border-t border-slate-100">
            {center.gender && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {center.gender === 'hombres' || center.gender === 'hombre' ? 'Hombres' : center.gender === 'mujeres' || center.gender === 'mujer' ? 'Mujeres' : 'Mixto'}
              </span>
            )}
            {center.whatsapp && (
              <span className="flex items-center gap-1 text-green-600 ml-auto">
                <MessageCircle className="w-3 h-3" />WhatsApp
              </span>
            )}
          </div>
          
          {/* Compare button */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <CompareCheckbox center={center} />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// FAQ Data for SEO
const faqData = [
  {
    question: "¿Qué tipos de centros de rehabilitación de drogas existen en Chile?",
    answer: "En Chile existen centros de rehabilitación residenciales (internación completa), ambulatorios (tratamiento sin internación), semi-residenciales (modalidad mixta) y tratamientos online. Cada modalidad se adapta a diferentes necesidades, severidad de la adicción y situación familiar del paciente."
  },
  {
    question: "¿Cuánto cuesta un centro de rehabilitación de drogas en Chile?",
    answer: "Los costos varían según la modalidad y servicios. Los centros ambulatorios pueden costar desde $300.000 CLP mensuales, mientras que los residenciales oscilan entre $800.000 y $3.000.000 CLP mensuales. También existen opciones gratuitas a través de SENDA y centros con convenio estatal."
  },
  {
    question: "¿Cuánto dura un tratamiento de rehabilitación de drogas?",
    answer: "La duración promedio es de 3 a 12 meses, dependiendo del tipo de adicción, severidad y respuesta al tratamiento. Los programas residenciales suelen durar entre 6 y 12 meses, mientras que los ambulatorios pueden extenderse según el progreso del paciente."
  },
  {
    question: "¿Cómo elegir el mejor centro de rehabilitación para un familiar?",
    answer: "Es importante considerar: la especialización del centro según el tipo de adicción, la modalidad de tratamiento (residencial vs ambulatorio), ubicación geográfica, costo, acreditación y certificaciones, metodología terapéutica, y las reseñas de otros pacientes. Recomendamos visitar varios centros antes de decidir."
  },
  {
    question: "¿Los centros de rehabilitación en Chile aceptan personas con adicción a alcohol y drogas simultáneamente?",
    answer: "Sí, la mayoría de los centros de rehabilitación en Chile están preparados para tratar adicciones múltiples (politoxicomanía). De hecho, es común que los pacientes presenten dependencia a más de una sustancia. Los equipos multidisciplinarios diseñan planes de tratamiento integrales."
  },
  {
    question: "¿Existen centros de rehabilitación gratuitos en Chile?",
    answer: "Sí, SENDA (Servicio Nacional para la Prevención y Rehabilitación del Consumo de Drogas y Alcohol) ofrece programas gratuitos a través de su red de centros públicos. Además, algunos centros privados tienen convenios con FONASA o cupos de ayuda social."
  }
];

// FAQ Component
function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);
  
  return (
    <section className="py-12 md:py-16 border-t border-slate-800">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-50 mb-2 text-center">
          Preguntas Frecuentes sobre Centros de Rehabilitación de Drogas en Chile
        </h2>
        <p className="text-slate-400 text-center mb-8">
          Resolvemos las dudas más comunes sobre el tratamiento de adicciones
        </p>
        
        <div className="space-y-3">
          {faqData.map(function(faq, index) {
            var isOpen = openIndex === index;
            return (
              <div 
                key={index} 
                className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
              >
                <button
                  onClick={function() { setOpenIndex(isOpen ? null : index); }}
                  className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-800/50 transition-colors"
                  data-testid={'faq-question-' + index}
                >
                  <span className="font-medium text-slate-200">{faq.question}</span>
                  <ChevronDown className={'w-5 h-5 text-emerald-400 flex-shrink-0 transition-transform ' + (isOpen ? 'rotate-180' : '')} />
                </button>
                {isOpen && (
                  <div className="px-6 pb-4">
                    <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* FAQ Schema for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(function(faq) {
          return {
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          };
        })
      })}} />
    </section>
  );
}

export function CentersDirectory() {
  const [searchParams] = useSearchParams();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Initialize from URL - use a function to ensure correct initial value
  const getInitialRegion = function() {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('region') || '';
    }
    return '';
  };
  const getInitialModality = function() {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('modality') || '';
    }
    return '';
  };
  
  const [region, setRegion] = useState(getInitialRegion);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Advanced filters
  const [modality, setModality] = useState(getInitialModality);
  const [gender, setGender] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  useEffect(function() {
    async function load() {
      setLoading(true);
      try {
        var url = API + '/centers/public?page=' + page + '&limit=12';
        if (search) url += '&search=' + encodeURIComponent(search);
        if (region && region !== 'all') url += '&region=' + encodeURIComponent(region);
        if (modality && modality !== 'all') url += '&modality=' + modality;
        if (gender && gender !== 'all') url += '&gender=' + gender;
        if (priceMin) url += '&price_min=' + priceMin;
        if (priceMax) url += '&price_max=' + priceMax;
        if (verifiedOnly) url += '&is_verified=true';
        var res = await axios.get(url);
        setCenters(res.data.centers || []);
        setTotalPages(res.data.pages || 1);
        setTotal(res.data.total || 0);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [page, region, search, modality, gender, priceMin, priceMax, verifiedOnly]);

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
  }

  function clearFilters() {
    setRegion('');
    setSearch('');
    setModality('');
    setGender('');
    setPriceMin('');
    setPriceMax('');
    setVerifiedOnly(false);
    setPage(1);
  }

  var hasActiveFilters = region || modality || gender || priceMin || priceMax || verifiedOnly;

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Centros de Rehabilitación de Drogas en Chile 2026 | Directorio Completo</title>
        <meta name="description" content="Encuentra los mejores centros de rehabilitación de drogas en Chile. Compara precios, modalidades (residencial, ambulatorio), ubicaciones y reseñas. Guía actualizada 2026 con más de 200 centros verificados." />
        <meta name="keywords" content="centros de rehabilitación de drogas en chile, rehabilitación drogas chile, centros rehabilitación alcohol, clínicas adicciones chile, tratamiento drogas santiago" />
        <meta property="og:title" content="Centros de Rehabilitación de Drogas en Chile | Directorio 2026" />
        <meta property="og:description" content="Directorio completo de centros de rehabilitación de drogas y alcohol en Chile. Encuentra el tratamiento ideal según tu presupuesto, ubicación y necesidades." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Centros de Rehabilitación de Drogas en Chile | Directorio 2026" />
        <meta name="twitter:description" content="Directorio completo. Compara precios, modalidades y ubicaciones." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile" />
        
        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
            { "@type": "ListItem", "position": 2, "name": "Centros de Rehabilitación de Drogas en Chile" }
          ]
        })}</script>
        
        {/* ItemList Schema for directory */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Centros de Rehabilitación de Drogas en Chile",
          "description": "Directorio de centros especializados en tratamiento de adicciones a drogas y alcohol en Chile",
          "numberOfItems": total || 200,
          "itemListOrder": "https://schema.org/ItemListOrderDescending"
        })}</script>
      
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-6 md:pb-8 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-4 md:mb-6">
            <h1 className="text-2xl md:text-4xl font-bold text-slate-50 mb-2" data-testid="centers-title">
              Centros de Rehabilitación de Drogas en Chile
            </h1>
            <p className="text-sm md:text-base text-slate-400">
              Encuentra y compara los mejores centros de tratamiento para adicciones en todo Chile
            </p>
          </div>
          
          {/* Search Form */}
          <form onSubmit={onSearch} className="max-w-3xl mx-auto">
            <div className="flex gap-2 md:gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-slate-500" />
                <Input
                  value={search}
                  onChange={function(e) { setSearch(e.target.value); }}
                  placeholder="Buscar centros..."
                  className="pl-9 md:pl-12 h-10 md:h-12 bg-slate-900 border-slate-700 text-slate-200"
                  data-testid="centers-search-input"
                />
              </div>
              <Button type="submit" className="h-10 md:h-12 px-4 md:px-8 bg-emerald-500 hover:bg-emerald-600">
                <Search className="w-4 h-4 md:mr-2" /><span className="hidden md:inline">Buscar</span>
              </Button>
            </div>
          </form>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-6 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen} className="lg:hidden mb-4">
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between border-slate-700 text-slate-300">
                      <span className="flex items-center gap-2"><Filter className="w-4 h-4" /> Filtros {hasActiveFilters ? '•' : ''}</span>
                      <ChevronDown className={'w-4 h-4 transition-transform ' + (filtersOpen ? 'rotate-180' : '')} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <FiltersContent
                      region={region} setRegion={setRegion}
                      modality={modality} setModality={setModality}
                      gender={gender} setGender={setGender}
                      priceMin={priceMin} setPriceMin={setPriceMin}
                      priceMax={priceMax} setPriceMax={setPriceMax}
                      verifiedOnly={verifiedOnly} setVerifiedOnly={setVerifiedOnly}
                      clearFilters={clearFilters}
                      setPage={setPage}
                    />
                  </CollapsibleContent>
                </Collapsible>
                
                <div className="hidden lg:block">
                  <h3 className="text-lg font-semibold text-slate-50 mb-4 flex items-center gap-2">
                    <Filter className="w-5 h-5" /> Filtros
                  </h3>
                  <FiltersContent
                    region={region} setRegion={setRegion}
                    modality={modality} setModality={setModality}
                    gender={gender} setGender={setGender}
                    priceMin={priceMin} setPriceMin={setPriceMin}
                    priceMax={priceMax} setPriceMax={setPriceMax}
                    verifiedOnly={verifiedOnly} setVerifiedOnly={setVerifiedOnly}
                    clearFilters={clearFilters}
                    setPage={setPage}
                  />
                </div>
              </div>
            </aside>
            
            {/* Results */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <p className="text-slate-400">
                  {loading ? 'Cargando...' : total + ' centros encontrados'}
                </p>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : centers.length === 0 ? (
                <div className="text-center py-20">
                  <Building2 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-400 mb-2">No se encontraron centros</h3>
                  <p className="text-slate-500 mb-4">Intenta ajustar los filtros de búsqueda</p>
                  <Button onClick={clearFilters} variant="outline" className="border-slate-700 text-slate-300">
                    Limpiar filtros
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="centers-grid">
                    {centers.map(function(center) {
                      return <CenterCard key={center.center_id} center={center} />;
                    })}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-10">
                      <Button
                        onClick={function() { setPage(Math.max(1, page - 1)); }}
                        disabled={page === 1}
                        variant="outline"
                        className="border-slate-700 text-slate-300 disabled:opacity-50"
                      >
                        Anterior
                      </Button>
                      <span className="flex items-center px-4 text-slate-400">
                        Página {page} de {totalPages}
                      </span>
                      <Button
                        onClick={function() { setPage(Math.min(totalPages, page + 1)); }}
                        disabled={page === totalPages}
                        variant="outline"
                        className="border-slate-700 text-slate-300 disabled:opacity-50"
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section for SEO */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <FAQSection />
      </div>

      {/* SEO interno · enlazado a ciudades y regiones (exact match anchors) */}
      <CentersByLocationLinks variant="general" testId="centers-directory-by-location" />

      <Footer />
    </div>
  );
}
export function CenterDetail() {
  var params = useParams();
  var paramId = params.centerId;
  var [center, setCenter] = useState(null);
  var [loading, setLoading] = useState(true);
  var [activeTab, setActiveTab] = useState('info');
  var [selectedImage, setSelectedImage] = useState(null);
  var [showPhone, setShowPhone] = useState(false);
  var [centerId, setCenterId] = useState(null);
  var [error, setError] = useState(null);
  var [branchInfo, setBranchInfo] = useState(null);

  function handleRevealPhone() {
    setShowPhone(true);
    if (centerId) trackPhoneReveal('center', centerId);
  }

  useEffect(function() {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        var res;
        // Check if paramId is a slug (no underscore) or center_id (has underscore)
        if (paramId && paramId.startsWith('center_')) {
          // It's a center_id
          res = await axios.get(API + '/centers/public/' + paramId);
        } else if (paramId) {
          // It's a slug, use by-slug endpoint
          res = await axios.get(API + '/centers/by-slug/' + paramId);
        } else {
          throw new Error('No center ID provided');
        }
        if (res && res.data) {
          setCenter(res.data);
          setCenterId(res.data.center_id);
          if (res.data.center_id) trackPageView('center', res.data.center_id);
          // Load branch info
          try {
            var branchRes = await axios.get(API + '/center/branch-info/' + res.data.center_id);
            setBranchInfo(branchRes.data);
          } catch (be) {}
        }
      } catch (e) {
        console.error('Error loading center:', e);
        setError(e.message || 'Error loading center');
        setCenter(null);
      }
      setLoading(false);
    }
    load();
  }, [paramId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </div>
    );
  }

  if (!center) {
    return (
      <div className="min-h-screen bg-slate-950">
        <title>Centro no encontrado (404) | SinAdicciones.org</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="El centro de rehabilitación que buscas no existe o ha sido removido del directorio." />
        <Header />
        <div className="flex flex-col items-center justify-center py-40">
          <Building2 className="w-16 h-16 text-slate-700 mb-4" />
          <h2 className="text-2xl text-slate-50 mb-2">Centro no encontrado</h2>
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
          <p className="text-slate-400 text-sm mb-4">URL: {paramId}</p>
          <Button asChild variant="outline" className="mt-4 border-slate-700 text-slate-300 hover:bg-slate-800">
            <Link to="/centros-de-rehabilitacion-drogas-chile"><ArrowLeft className="w-4 h-4 mr-2" />Volver al directorio</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Combine all images and remove duplicates - apply getImageUrl to all
  var allImagesList = [center.logo, ...(center.gallery || []), ...(center.images || [])].filter(Boolean).map(getImageUrl).filter(Boolean);
  var allImages = allImagesList.filter(function(img, index, self) {
    return self.indexOf(img) === index;
  });
  var mainLogo = getImageUrl(center.logo);
  // Guard defensivo: si el fetch devolvió objeto pero sin nombre (edge case en snapshots
  // pre-renderizados con backend lento), fallback a título/canonical genéricos para NO
  // publicar SEO corrupto ("undefined - Centro..." o canonical /centros/undefined).
  var safeName = (center.name && typeof center.name === 'string') ? center.name : null;
  var safeSlug = (center.slug || center.center_id || paramId || '');
  var seoTitle = safeName ? (safeName + ' - Centro de Rehabilitación | Sin Adicciones') : 'Centro de Rehabilitación | Sin Adicciones.org';
  var seoDescription = center.description ? center.description.substring(0, 160) : 'Centro de rehabilitación de adicciones en ' + (center.city || 'Chile');
  var seoImage = mainLogo || (allImages.length > 0 ? allImages[0] : '');
  var canonicalUrl = 'https://sinadicciones.org/centros/' + safeSlug;

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{seoTitle}</title>
        {!safeName && <meta name="robots" content="noindex, follow" />}
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={safeName ? (safeName + ' - Centro de Rehabilitación') : 'Centro de Rehabilitación'} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="business.business" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content="es_CL" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={center.name} />
        <meta name="twitter:description" content={seoDescription} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
        <link rel="canonical" href={'https://sinadicciones.org/centros/' + (center.slug || center.center_id)} />
        
        {/* BreadcrumbList Schema for SEO */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org/" },
            { "@type": "ListItem", "position": 2, "name": "Centros de Rehabilitación", "item": "https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile" },
            { "@type": "ListItem", "position": 3, "name": center.city || "Chile", "item": "https://sinadicciones.org/centros?city=" + encodeURIComponent(center.city || '') },
            { "@type": "ListItem", "position": 4, "name": center.name, "item": "https://sinadicciones.org/centros/" + (center.slug || center.center_id) }
          ]
        })}</script>
        
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["MedicalBusiness", "LocalBusiness"],
          "name": center.name,
          "description": center.description,
          "address": { "@type": "PostalAddress", "addressLocality": center.city, "streetAddress": center.address, "addressRegion": center.region || "Chile", "addressCountry": "CL" },
          "telephone": center.phone,
          "email": center.email || undefined,
          "url": "https://sinadicciones.org/centros/" + (center.slug || center.center_id),
          "image": seoImage || undefined,
          "logo": mainLogo || undefined,
          "priceRange": (center.price_min && center.price_max) ? ('$' + Number(center.price_min).toLocaleString() + ' - $' + Number(center.price_max).toLocaleString()) : "$$",
          "medicalSpecialty": "AddictionMedicine",
          "isAcceptingNewPatients": true,
          "areaServed": { "@type": "Country", "name": "Chile" },
          "geo": (center.coordinates && center.coordinates.lat && center.coordinates.lng) ? {
            "@type": "GeoCoordinates",
            "latitude": center.coordinates.lat,
            "longitude": center.coordinates.lng
          } : undefined,
          "openingHours": center.schedule || undefined,
          "sameAs": [center.website, center.facebook, center.instagram].filter(Boolean),
          "aggregateRating": (center.average_rating && center.average_rating > 0) ? { "@type": "AggregateRating", "ratingValue": center.average_rating, "reviewCount": center.total_reviews || 0 } : undefined
        })}</script>
      
      <Header />
      <main className="pt-20 pb-16">
        {/* Breadcrumbs visual */}
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-3">
            <nav className="flex items-center gap-2 text-sm text-slate-400 mb-2">
              <Link to="/" className="hover:text-emerald-400 flex items-center gap-1"><Home className="w-3 h-3" />Inicio</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/centros-de-rehabilitacion-drogas-chile" className="hover:text-emerald-400">Centros</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={'/centros?city=' + encodeURIComponent(center.city || '')} className="hover:text-emerald-400">{center.city}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-300 truncate max-w-[200px]">{center.name}</span>
            </nav>
            <Button asChild variant="ghost" size="sm" className="text-slate-400 hover:text-emerald-400 hover:bg-transparent -ml-2">
              <Link to="/centros-de-rehabilitacion-drogas-chile"><ArrowLeft className="w-4 h-4 mr-2" />Volver al directorio</Link>
            </Button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-8">
          {/* Header with main image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 mb-6 sm:mb-8">
            <div className="space-y-3 sm:space-y-4">
              <div className="aspect-video bg-slate-800 rounded-xl overflow-hidden flex items-center justify-center">
                {selectedImage || mainLogo ? (
                  <img src={selectedImage || mainLogo} alt={center.name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-24 h-24 text-slate-700" />
                )}
              </div>
              {allImages.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-700">
                  {allImages.map(function(img, i) {
                    return (
                      <button key={i} onClick={function() { setSelectedImage(img); }} className={'flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-colors ' + (selectedImage === img || (!selectedImage && i === 0) ? 'border-emerald-500' : 'border-slate-700 hover:border-slate-600')}>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-50 mb-2 break-words" data-testid="center-name">{center.name}</h1>
                  <div className="flex items-start gap-2 text-slate-400 text-sm sm:text-base">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
                    <span className="break-words">{center.address}{center.city ? ', ' + center.city : ''}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 flex-shrink-0">
                  {center.plan_level >= 3 && (
                    <span className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-500/10 text-purple-400 rounded-lg text-xs sm:text-sm font-medium border border-purple-500/30" data-testid="badge-elite">
                      <Crown className="w-3 h-3 sm:w-4 sm:h-4" />Elite
                    </span>
                  )}
                  {center.plan_level === 2 && (
                    <span className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-amber-500/10 text-amber-400 rounded-lg text-xs sm:text-sm font-medium border border-amber-500/30">
                      <Crown className="w-3 h-3 sm:w-4 sm:h-4" />Premium
                    </span>
                  )}
                  {center.plan_level === 1 && (
                    <span className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-500/10 text-blue-400 rounded-lg text-xs sm:text-sm font-medium border border-blue-500/30">
                      <Crown className="w-3 h-3 sm:w-4 sm:h-4" />Destacado
                    </span>
                  )}
                  {center.is_verified && (
                    <span className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs sm:text-sm font-medium flex-shrink-0 w-fit">
                      <BadgeCheck className="w-3 h-3 sm:w-4 sm:h-4" />Verificado
                    </span>
                  )}
                </div>
              </div>
              {center.average_rating > 0 && (
                <div className="flex items-center gap-3 mb-4">
                  <StarRating rating={Math.round(center.average_rating)} size="lg" />
                  <span className="text-slate-400">{center.average_rating.toFixed(1)} ({center.total_reviews} reseñas)</span>
                </div>
              )}
              {center.description && <p className="text-slate-400 mb-6 leading-relaxed text-sm sm:text-base">{center.description}</p>}
              
              {/* Quick contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <WhatsAppButton number={center.whatsapp} name={center.name} targetType="center" targetId={centerId} />
                <a href={'tel:' + center.phone} onClick={function() { trackCallClick('center', centerId); }} className="flex items-center justify-center gap-3 p-3 sm:p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
                  <Phone className="w-5 h-5 text-emerald-400" /><span>Llamar</span>
                </a>
              </div>
            </div>
          </div>

          {/* Branch / Parent relationship */}
          {branchInfo && branchInfo.is_branch && branchInfo.parent && (
            <div className="mb-6 p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <GitBranch className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium">Sucursal de {branchInfo.branch_of || branchInfo.parent.name}</span>
              </div>
              <Link to={'/centros/' + (branchInfo.parent.slug || branchInfo.parent.center_id)} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
                <Building2 className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-slate-200 font-medium text-sm">{branchInfo.parent.name}</p>
                  <p className="text-slate-500 text-xs">{branchInfo.parent.city || 'Centro principal'}</p>
                </div>
              </Link>
              {branchInfo.siblings && branchInfo.siblings.length > 0 && (
                <div className="mt-3">
                  <p className="text-slate-500 text-xs mb-2">Otras sucursales:</p>
                  <div className="flex flex-wrap gap-2">
                    {branchInfo.siblings.map(function(s) {
                      return (
                        <Link key={s.center_id} to={'/centros/' + (s.slug || s.center_id)} className="px-3 py-1.5 bg-slate-800/50 rounded-lg text-xs text-slate-300 hover:bg-slate-700 transition-colors">
                          {s.name} ({s.city})
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {branchInfo && !branchInfo.is_branch && branchInfo.branches && branchInfo.branches.length > 0 && (
            <div className="mb-6 p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <GitBranch className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium">Sucursales ({branchInfo.branches.length})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {branchInfo.branches.map(function(b) {
                  return (
                    <Link key={b.center_id} to={'/centros/' + (b.slug || b.center_id)} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
                      <Building2 className="w-4 h-4 text-purple-400" />
                      <div>
                        <p className="text-slate-200 font-medium text-sm">{b.name}</p>
                        <p className="text-slate-500 text-xs">{b.city}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 border-b border-slate-800 mb-6">
            <button onClick={function() { setActiveTab('info'); }} className={'px-4 py-3 font-medium transition-colors ' + (activeTab === 'info' ? 'text-emerald-400 border-b-2 border-emerald-500' : 'text-slate-400 hover:text-slate-300')}>Información</button>
            {center.youtube_video && <button onClick={function() { setActiveTab('video'); }} className={'px-4 py-3 font-medium transition-colors flex items-center gap-2 ' + (activeTab === 'video' ? 'text-emerald-400 border-b-2 border-emerald-500' : 'text-slate-400 hover:text-slate-300')}><Play className="w-4 h-4" />Video</button>}
            <button onClick={function() { setActiveTab('reviews'); }} className={'px-4 py-3 font-medium transition-colors flex items-center gap-2 ' + (activeTab === 'reviews' ? 'text-emerald-400 border-b-2 border-emerald-500' : 'text-slate-400 hover:text-slate-300')}><Star className="w-4 h-4" />Reseñas</button>
          </div>

          {/* Tab content */}
          {activeTab === 'info' && (
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                {/* Price & Duration */}
                {(center.price_min || center.price_max || center.treatment_duration_min || center.treatment_duration_max) && (
                  <div className="grid grid-cols-2 gap-4">
                    {(center.price_min || center.price_max) && (
                      <div className="p-4 bg-slate-800/50 rounded-xl">
                        <div className="flex items-center gap-2 text-emerald-400 mb-2">
                          <DollarSign className="w-5 h-5" /><span className="font-medium">Precio</span>
                        </div>
                        <p className="text-slate-300">
                          {formatPrice(center.price_min)}{center.price_max ? ' - ' + formatPrice(center.price_max) : '+'}
                        </p>
                      </div>
                    )}
                    {(center.treatment_duration_min || center.treatment_duration_max) && (
                      <div className="p-4 bg-slate-800/50 rounded-xl">
                        <div className="flex items-center gap-2 text-emerald-400 mb-2">
                          <Clock className="w-5 h-5" /><span className="font-medium">Duración</span>
                        </div>
                        <p className="text-slate-300">
                          {center.treatment_duration_min || '?'} - {center.treatment_duration_max || '?'} meses
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Modality, Gender & Capacity */}
                {(center.modality?.length > 0 || center.gender || center.capacity) && (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {center.modality && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-3">Modalidad</h3>
                        <div className="flex flex-wrap gap-2">
                          {parseModality(center.modality).map(function(m, i) { return <InfoBadge key={i} tipo="modalidad" valor={m} />; })}
                        </div>
                      </div>
                    )}
                    {center.gender && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-3">Género</h3>
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-slate-500" />
                          <span className="text-slate-300">{genderLabels[center.gender] || center.gender}</span>
                        </div>
                      </div>
                    )}
                    {center.capacity && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-3">Capacidad</h3>
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-emerald-400" />
                          <span className="text-slate-300">{center.capacity} pacientes</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Therapy Approaches / Treatment Type */}
                {center.therapy_approaches?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-50 mb-3">Tipo de Tratamiento</h3>
                    <div className="flex flex-wrap gap-2">
                      {center.therapy_approaches.map(function(t, i) { return <InfoBadge key={i} tipo="enfoque" valor={t} />; })}
                    </div>
                  </div>
                )}

                {/* Addictions Treated - check both fields for backwards compatibility */}
                {((center.addictions_treated && center.addictions_treated.length > 0) || (center.specializations && center.specializations.length > 0)) && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-50 mb-4">Adicciones que tratan</h3>
                    <div className="flex flex-wrap gap-2">
                      {(center.addictions_treated || center.specializations || []).map(function(s, i) { return <span key={i} className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg text-sm">{s}</span>; })}
                    </div>
                  </div>
                )}

                {center.services && center.services.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-50 mb-4">Servicios</h3>
                    <div className="flex flex-wrap gap-2">
                      {center.services.map(function(s, i) { return <span key={i} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-sm">{s}</span>; })}
                    </div>
                  </div>
                )}

                {/* Workshops */}
                {(center.workshops?.length > 0 || center.custom_workshops) && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-50 mb-4">Talleres</h3>
                    <div className="flex flex-wrap gap-2">
                      {(center.workshops || []).map(function(w, i) { return <span key={i} className="px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-sm">{w}</span>; })}
                      {center.custom_workshops && center.custom_workshops.split(',').map(function(w, i) { return <span key={'custom-' + i} className="px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-sm">{w.trim()}</span>; })}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-50 mb-4">Contacto</h3>
                <GoogleMapsLink address={center.address} city={center.city} targetType="center" targetId={centerId} />
                {center.phone && (
                  showPhone ? (
                    <a href={'tel:' + center.phone} onClick={function() { trackCallClick('center', centerId); }} className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
                      <Phone className="w-5 h-5 text-emerald-400" /><span>{center.phone}</span>
                    </a>
                  ) : (
                    <button onClick={handleRevealPhone} className="flex items-center gap-3 p-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors duration-200 text-white w-full">
                      <Eye className="w-5 h-5" /><span>Ver Número de Teléfono</span>
                    </button>
                  )
                )}
                {center.website && (
                  <a href={center.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
                    <Globe className="w-5 h-5 text-emerald-400" /><span>Sitio web</span><ExternalLink className="w-4 h-4 ml-auto" />
                  </a>
                )}
                <WhatsAppButton number={center.whatsapp} name={center.name} targetType="center" targetId={centerId} />
                
                {/* Social Media */}
                {(center.facebook || center.instagram) && (
                  <div className="pt-4 border-t border-slate-700">
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Redes Sociales</h4>
                    <div className="flex gap-3">
                      {center.facebook && (
                        <a href={center.facebook.startsWith('http') ? center.facebook : 'https://facebook.com/' + center.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                          <span className="text-sm">Facebook</span>
                        </a>
                      )}
                      {center.instagram && (
                        <a href={center.instagram.startsWith('http') ? center.instagram : 'https://instagram.com/' + center.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-pink-600/20 text-pink-400 rounded-lg hover:bg-pink-600/30 transition-colors">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                          <span className="text-sm">Instagram</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="mt-4">
                  <ContactForm targetType="center" targetId={centerId} targetName={center.name} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'video' && center.youtube_video && (
            <YouTubeEmbed url={center.youtube_video} />
          )}

          {activeTab === 'reviews' && (
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-slate-50 mb-4">Reseñas</h3>
                <ReviewsList targetType="center" targetId={centerId} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-50 mb-4">Deja tu reseña</h3>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-6">
                    <ReviewForm targetType="center" targetId={centerId} />
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
